import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Box, Button, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import GameScreenShell from "@components/Home/Games/GameScreenShell";
import SessionSummary, { SessionSummaryStats } from "@components/Home/Games/SessionSummary";
import type { GameSessionGame } from "@models/game";
import { GAME_IDS } from "@models/game";
import {
  useStartSessionMutation,
  useSubmitRoundMutation,
  useCompleteSessionMutation,
  useGetProgressQuery,
  useGetContentQuery,
} from "@store/gameApi";
import { RootState } from "@store/store";
import {
  type NBackStimulusItem,
  getStimulusPool,
  buildNBackSequence,
} from "./nBackContent";
import { getContentBankLabel } from "@utils/contentBankTranslations";

export interface NBackGuidedProps {
  guidedSessionId?: string;
  guidedCurrentGame?: GameSessionGame;
  guidedGameIndex?: number;
  guidedTotalGames?: number;
  onGuidedGameComplete?: () => void;
}

const ISI_MS = 500;
const ROUNDS_PER_SESSION = 3;
const SESSION_START_DEDUPE_MS = 2000;

let lastSessionStartAt = 0;

type Phase = "idle" | "playing" | "feedback";

function getLevelParams(level: number) {
  const clamped = Math.max(1, Math.min(10, level));
  if (clamped <= 2)
    return { n: 1, sequenceLength: 15, displayMs: 2000, showLabels: true, showHistory: true };
  if (clamped <= 4)
    return { n: 1, sequenceLength: 20, displayMs: 2000, showLabels: false, showHistory: true };
  if (clamped <= 6)
    return { n: 2, sequenceLength: 15, displayMs: 2000, showLabels: false, showHistory: false };
  if (clamped <= 8)
    return { n: 2, sequenceLength: 20, displayMs: 1500, showLabels: false, showHistory: false };
  const n = clamped === 10 ? 4 : 3;
  return { n, sequenceLength: 20, displayMs: 1200, showLabels: false, showHistory: false };
}

interface FreePlayLocationState {
  freePlaySessionId?: string;
  freePlayGames?: GameSessionGame[];
}

const NBack: FC<NBackGuidedProps> = ({
  guidedSessionId,
  guidedCurrentGame,
  guidedGameIndex = 0,
  guidedTotalGames = 1,
  onGuidedGameComplete,
}) => {
  const location = useLocation();
  const freePlayState = location.state as FreePlayLocationState | null;
  const freePlaySessionId = freePlayState?.freePlaySessionId;
  const freePlayGame = freePlayState?.freePlayGames?.[0];

  const isGuided = Boolean(guidedSessionId && guidedCurrentGame);
  const isFreePlayFromLibrary = Boolean(freePlaySessionId && freePlayGame);
  const effectiveSessionId = guidedSessionId ?? freePlaySessionId ?? null;
  const effectiveGame = guidedCurrentGame ?? freePlayGame;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId
  );
  const sessionStartedRef = useRef(false);
  const [startSession] = useStartSessionMutation();
  const [sessionStartError, setSessionStartError] = useState<string | null>(null);
  const [submitRound] = useSubmitRoundMutation();
  const [completeSession] = useCompleteSessionMutation();
  const { data: progressData } = useGetProgressQuery(
    { patientId: selectedPatientId! },
    { skip: !selectedPatientId }
  );

  const contentSeed = effectiveGame?.contentSeed ?? "";
  const difficulty = effectiveGame ? Math.max(1, Math.min(10, effectiveGame.difficulty)) : 1;
  const levelParams = useMemo(() => getLevelParams(difficulty), [difficulty]);

  const { data: contentData, error: contentError } = useGetContentQuery(
    { gameId: GAME_IDS.nBack, seed: contentSeed },
    { skip: !contentSeed }
  );

  const pool = useMemo(
    () => getStimulusPool(contentSeed, 0, contentData, contentError),
    [contentSeed, contentData, contentError]
  );

  const [sessionId, setSessionId] = useState<string | null>(effectiveSessionId);
  const [phase, setPhase] = useState<Phase>("idle");
  const [roundCount, setRoundCount] = useState(0);
  const [stimulusSequence, setStimulusSequence] = useState<number[]>([]);
  const [matchPositionsSet, setMatchPositionsSet] = useState<Set<number>>(new Set());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [roundStartTime, setRoundStartTime] = useState(0);
  const [stimulusOnAt, setStimulusOnAt] = useState(0);
  const [truePositives, setTruePositives] = useState(0);
  const [falsePositives, setFalsePositives] = useState(0);
  const [reactionTimes, setReactionTimes] = useState<number[]>([]);
  const [tapFeedback, setTapFeedback] = useState<"none" | "correct" | "falsePositive">("none");
  const [xpEarned, setXpEarned] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [sessionScore, setSessionScore] = useState(0);
  const [roundResults, setRoundResults] = useState<{ accuracy: number; reactionMs: number }[]>([]);
  const [domainUpdatesSum, setDomainUpdatesSum] = useState<Record<string, number>>({});
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  const [summaryStats, setSummaryStats] = useState<SessionSummaryStats | null>(null);
  const [muted, setMuted] = useState(false);
  const [imageLoadFailed, setImageLoadFailed] = useState<Set<string>>(new Set());

  const level = progressData?.level ?? 1;

  const startNewRound = useCallback(() => {
    const seed = contentSeed ? `${contentSeed}-${roundCount}` : `local-${roundCount}`;
    const { n, sequenceLength } = levelParams;
    const { sequence, matchPositions } = buildNBackSequence(
      pool.length,
      n,
      sequenceLength,
      seed
    );
    const now = Date.now();
    setStimulusSequence(sequence);
    setMatchPositionsSet(matchPositions);
    setCurrentIndex(0);
    setRoundStartTime(now);
    setTruePositives(0);
    setFalsePositives(0);
    setReactionTimes([]);
    setTapFeedback("none");
    setXpEarned(null);
    setPhase("playing");
    setStimulusOnAt(now);
    setImageLoadFailed(new Set());
  }, [contentSeed, roundCount, levelParams, pool.length]);

  useEffect(() => {
    if (isGuided || isFreePlayFromLibrary) return;
    if (!selectedPatientId) {
      setSessionStartError(t("games.errors.onlyPatientCanPlay"));
      return;
    }
    if (sessionStartedRef.current) return;
    const now = Date.now();
    if (now - lastSessionStartAt < SESSION_START_DEDUPE_MS) return;
    lastSessionStartAt = now;
    sessionStartedRef.current = true;
    setSessionStartError(null);
    let mounted = true;
    startSession({ patientId: selectedPatientId })
      .unwrap()
      .then((res) => {
        if (mounted && res?.sessionId) {
          setSessionId(res.sessionId);
          setSessionStartTime(Date.now());
        }
      })
      .catch((err: { status?: number }) => {
        if (mounted && err?.status === 403) {
          setSessionStartError(t("games.errors.onlyPatientCanPlay"));
        }
      });
    return () => {
      mounted = false;
    };
  }, [isGuided, isFreePlayFromLibrary, selectedPatientId, startSession, t]);

  useEffect(() => {
    if ((isGuided && guidedSessionId) || isFreePlayFromLibrary) {
      setSessionStartTime(Date.now());
    }
  }, [isGuided, guidedSessionId, isFreePlayFromLibrary]);

  useEffect(() => {
    if (sessionStartTime == null || paused) return;
    const id = setInterval(() => {
      setTimerSeconds(Math.floor((Date.now() - sessionStartTime) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [sessionStartTime, paused]);

  // Use a polling interval so progress isn't cancelled by timer/shell re-renders (which cleared timeouts)
  useEffect(() => {
    if (phase !== "playing" || stimulusSequence.length === 0 || paused) return;
    const displayMs = levelParams.displayMs;
    const interval = setInterval(() => {
      setCurrentIndex((idx) => {
        if (idx >= stimulusSequence.length) return idx;
        const displayStart = idx === 0 ? stimulusOnAt : stimulusOnAt + ISI_MS;
        const elapsed = Date.now() - displayStart;
        if (idx > 0 && elapsed < 0) return idx; // still in blank
        if (elapsed >= displayMs) {
          setStimulusOnAt(Date.now());
          return idx + 1;
        }
        return idx;
      });
    }, 100);
    return () => clearInterval(interval);
  }, [phase, stimulusSequence.length, stimulusOnAt, currentIndex, levelParams.displayMs, paused]);

  // When currentIndex reaches end, switch to feedback
  useEffect(() => {
    if (phase !== "playing" || stimulusSequence.length === 0) return;
    if (currentIndex >= stimulusSequence.length) {
      setPhase("feedback");
    }
  }, [phase, currentIndex, stimulusSequence.length]);

  const handleTapMatch = useCallback(() => {
    if (phase !== "playing" || currentIndex >= stimulusSequence.length) return;
    const isMatch = matchPositionsSet.has(currentIndex);
    const reactionMs = Date.now() - stimulusOnAt;

    if (isMatch) {
      setTruePositives((c) => c + 1);
      setReactionTimes((arr) => [...arr, reactionMs]);
      setTapFeedback("correct");
    } else {
      setFalsePositives((c) => c + 1);
      setTapFeedback("falsePositive");
    }
    setTimeout(() => setTapFeedback("none"), 400);
  }, [phase, currentIndex, stimulusSequence.length, matchPositionsSet, stimulusOnAt]);


  const handleNextRound = useCallback(() => {
    const nextRound = roundCount + 1;
    const missed = Math.max(0, matchPositionsSet.size - truePositives);
    const total = truePositives + falsePositives + missed;
    const accuracy = total > 0 ? truePositives / total : 0;
    const avgReactionMs =
      reactionTimes.length > 0
        ? reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length
        : 0;

    setRoundResults((prev) => [...prev, { accuracy, reactionMs: avgReactionMs }]);

    if (sessionId) {
      submitRound({
        sessionId,
        body: {
          gameId: GAME_IDS.nBack,
          roundNumber: nextRound,
          difficulty,
          accuracy,
          reactionTimeMs: reactionTimes.length > 0 ? Math.round(avgReactionMs) : null,
          completionTimeMs: Date.now() - roundStartTime,
          errorCount: falsePositives + missed,
          rawData: {
            truePositives,
            falsePositives,
            missedMatches: missed,
            reactionTimes,
          },
        },
      })
        .unwrap()
        .then((res) => {
          if (res?.xpEarned != null) {
            setXpEarned(res.xpEarned);
            setSessionScore((s) => s + res.xpEarned);
          }
          if (res?.domainUpdates && Object.keys(res.domainUpdates).length > 0) {
            setDomainUpdatesSum((prev) => {
              const next = { ...prev };
              for (const [k, v] of Object.entries(res.domainUpdates)) {
                next[k] = (next[k] ?? 0) + v;
              }
              return next;
            });
          }
        })
        .catch(() => {});
    } else {
      const xp = accuracy >= 0.8 ? 20 : 10;
      setXpEarned(xp);
      setSessionScore((s) => s + xp);
    }

    setRoundCount(nextRound);

    if (nextRound >= ROUNDS_PER_SESSION) {
      if (isGuided && onGuidedGameComplete) {
        onGuidedGameComplete();
        return;
      }
      const allResults = [...roundResults, { accuracy, reactionMs: avgReactionMs }];
      const totalRounds = allResults.length;
      const avgAccuracy =
        totalRounds > 0
          ? (allResults.reduce((s, r) => s + r.accuracy, 0) / totalRounds) * 100
          : 0;
      const avgReactionSec =
        totalRounds > 0
          ? allResults.reduce((s, r) => s + r.reactionMs, 0) / totalRounds / 1000
          : 0;

      if (sessionId) {
        completeSession({ sessionId })
          .unwrap()
          .then((res) => {
            setSummaryStats({
              accuracyPercent: avgAccuracy,
              avgReactionSeconds: avgReactionSec,
              xpEarned: res?.totalXp ?? sessionScore,
              domainChanges: domainUpdatesSum,
            });
          })
          .catch(() => {
            setSummaryStats({
              accuracyPercent: avgAccuracy,
              avgReactionSeconds: avgReactionSec,
              xpEarned: sessionScore,
              domainChanges: domainUpdatesSum,
            });
          });
      } else {
        setSummaryStats({
          accuracyPercent: avgAccuracy,
          avgReactionSeconds: avgReactionSec,
          xpEarned: sessionScore,
          domainChanges: domainUpdatesSum,
        });
      }
      lastSessionStartAt = 0;
      setSessionId(null);
      setShowSessionSummary(true);
      return;
    }
    startNewRound();
  }, [
    roundCount,
    truePositives,
    falsePositives,
    matchPositionsSet.size,
    reactionTimes,
    roundStartTime,
    sessionId,
    isGuided,
    onGuidedGameComplete,
    completeSession,
    submitRound,
    startNewRound,
    roundResults,
    sessionScore,
    domainUpdatesSum,
    difficulty,
  ]);

  const handleExit = useCallback(() => {
    if (sessionId) completeSession({ sessionId }).catch(() => {});
    lastSessionStartAt = 0;
    navigate(-1);
  }, [sessionId, completeSession, navigate]);

  const handleSummaryContinue = useCallback(() => navigate(-1), [navigate]);

  const currentStimulus: NBackStimulusItem | null =
    phase === "playing" &&
    currentIndex < stimulusSequence.length &&
    pool.length > 0
      ? pool[stimulusSequence[currentIndex] % pool.length]
      : null;

  const showStimulus = phase === "playing" && currentIndex < stimulusSequence.length;
  const isBlank =
    showStimulus &&
    currentIndex > 0 &&
    Date.now() - stimulusOnAt < ISI_MS;

  const feedbackText =
    phase === "feedback"
      ? t("games.nBack.feedbackCorrect")
      : tapFeedback === "falsePositive"
        ? t("games.nBack.feedbackFalsePositive")
        : null;

  const progress =
    phase === "idle"
      ? 0
      : (roundCount + (phase === "feedback" ? 1 : 0)) / ROUNDS_PER_SESSION;

  const nLabel = t("games.nBack.nBackLabel", { n: levelParams.n });
  const helperText =
    levelParams.n === 1
      ? t("games.nBack.helperText1")
      : t("games.nBack.helperTextN", { n: levelParams.n });

  if (showSessionSummary && summaryStats) {
    return (
      <SessionSummary stats={summaryStats} onContinue={handleSummaryContinue} />
    );
  }

  return (
    <GameScreenShell
      gameName={t("games.nBack.title")}
      level={level}
      progress={progress}
      feedback={feedbackText}
      timerSeconds={sessionStartTime != null ? timerSeconds : null}
      score={sessionScore > 0 ? sessionScore : null}
      paused={paused}
      onBack={handleExit}
      onPause={() => setPaused((p) => !p)}
      gameIndex={isGuided ? guidedGameIndex : undefined}
      totalGames={isGuided ? guidedTotalGames : undefined}
      muted={muted}
      onMuteChange={setMuted}
    >
      {phase === "idle" && (
        <Box
          textAlign="center"
          py={4}
          flex={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          {sessionStartError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {sessionStartError}
            </Typography>
          )}
          <Typography variant="h6" gutterBottom>
            {t("games.nBack.instructionsTitle")}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t("games.nBack.instructions")}
          </Typography>
          <Button
            variant="contained"
            size="medium"
            onClick={startNewRound}
            sx={{ minHeight: 48, minWidth: 180, alignSelf: "center" }}
          >
            {t("games.nBack.startRound")}
          </Button>
        </Box>
      )}

      {(phase === "playing" || phase === "feedback") && (
        <>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ fontSize: "1.5rem", mb: 0.5, textAlign: "center" }}
          >
            {nLabel}
          </Typography>
          {levelParams.n <= 3 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1.5, textAlign: "center" }}
            >
              {helperText}
            </Typography>
          )}
          {levelParams.n > 3 && <Box sx={{ mb: 1.5 }} />}

          <Box
            minHeight={220}
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ border: 1, borderColor: "divider", borderRadius: 2, bgcolor: "action.hover" }}
          >
            {showStimulus && !isBlank && currentStimulus && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                sx={{
                  animation: tapFeedback === "falsePositive" ? "nback-shake 0.4s ease" : undefined,
                  "@keyframes nback-shake": {
                    "0%,100%": { transform: "translateX(0)" },
                    "25%": { transform: "translateX(-6px)" },
                    "75%": { transform: "translateX(6px)" },
                  },
                }}
              >
                <Box
                  sx={{
                    width: 160,
                    height: 160,
                    borderRadius: 2,
                    bgcolor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {currentStimulus.imageUrl &&
                  !imageLoadFailed.has(currentStimulus.id) ? (
                    <Box
                      component="img"
                      src={currentStimulus.imageUrl.startsWith("/") ? process.env.PUBLIC_URL + currentStimulus.imageUrl : currentStimulus.imageUrl}
                      alt=""
                      onError={() =>
                        setImageLoadFailed((prev) => new Set(prev).add(currentStimulus.id))
                      }
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <Typography variant="h3" color="primary.contrastText">
                      {getContentBankLabel(t, currentStimulus.id, currentStimulus.label).slice(0, 1).toUpperCase()}
                    </Typography>
                  )}
                </Box>
                {levelParams.showLabels && (
                  <Typography variant="h6" sx={{ mt: 1 }}>
                    {getContentBankLabel(t, currentStimulus.id, currentStimulus.label)}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {levelParams.showHistory && phase === "playing" && levelParams.n > 0 && (
            <Box
              display="flex"
              justifyContent="center"
              gap={0.5}
              flexWrap="wrap"
              sx={{ mt: 1, minHeight: 48 }}
            >
              {Array.from({ length: levelParams.n }, (_, i) => {
                const idx = currentIndex - 1 - levelParams.n + i;
                const item =
                  idx >= 0 && idx < stimulusSequence.length && pool.length > 0
                    ? pool[stimulusSequence[idx] % pool.length]
                    : null;
                return (
                  <Box
                    key={i}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      bgcolor: item ? "primary.light" : "action.hover",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    {item &&
                      (item.imageUrl && !imageLoadFailed.has(item.id) ? (
                        <Box
                          component="img"
                          src={item.imageUrl.startsWith("/") ? process.env.PUBLIC_URL + item.imageUrl : item.imageUrl}
                          alt=""
                          onError={() =>
                            setImageLoadFailed((prev) => new Set(prev).add(item.id))
                          }
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="primary.contrastText">
                          {getContentBankLabel(t, item.id, item.label).slice(0, 1)}
                        </Typography>
                      ))}
                  </Box>
                );
              })}
            </Box>
          )}

          {phase === "playing" && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleTapMatch}
              sx={{
                minHeight: 56,
                minWidth: 200,
                mt: 2,
                mb: 2,
                alignSelf: "center",
                fontSize: "1.1rem",
              }}
            >
              {t("games.nBack.tapMatch")}
            </Button>
          )}

          {phase === "feedback" && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
              mt={2}
              mb={2}
            >
              {xpEarned != null && (
                <Typography variant="body2" color="primary.main">
                  +{xpEarned} XP
                </Typography>
              )}
              <Button
                variant="contained"
                size="medium"
                onClick={handleNextRound}
                sx={{ minHeight: 48, minWidth: 180 }}
              >
                {roundCount + 1 >= ROUNDS_PER_SESSION
                  ? t("games.nBack.finishSession")
                  : t("games.nBack.nextRound")}
              </Button>
            </Box>
          )}
        </>
      )}
    </GameScreenShell>
  );
};

export default NBack;
